import { Service } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
	CancelOrderResponse,
	ModifyOrderRequest,
	ModifyOrderResponse,
	MultipleCancelOrderResponse,
	SubmitOrderCommand,
	SubmitOrderResponse
} from '../models/order-api.model';
import { BaseService } from './base.service';
import { KalaApiResponse } from '../models/kala-api.model';

/**
 * Kala reports business failures as `200 OK` with `isSuccessful: false`, so the
 * envelope — not the HTTP status — decides whether a call actually worked.
 */
@Service()
export class OrdersService extends BaseService {
	private get ordersUrl(): string {
		return `${this.kalaApiPath}/orders`;
	}

	/**
	 * Resolves with one entry per exchange order created. The envelope succeeding
	 * only means the exchange answered — callers must still check `isAccepted` on
	 * each entry, since a submit can partially succeed.
	 */
	submitOrder(command: SubmitOrderCommand): Observable<SubmitOrderResponse[]> {
		return this.http.post<KalaApiResponse<SubmitOrderResponse[]>>(this.ordersUrl, command).pipe(
			map(response => {
				if (!response.isSuccessful) {
					throw new Error(response.error?.message ?? 'ثبت سفارش انجام نشد');
				}

				return response.data ?? [];
			})
		);
	}

	modifyOrder(orderId: number, request: ModifyOrderRequest): Observable<ModifyOrderResponse> {
		return this.http.put<KalaApiResponse<ModifyOrderResponse>>(`${this.ordersUrl}/${orderId}`, request).pipe(
			map(response => {
				if (!response.isSuccessful) {
					throw new Error(response.error?.message ?? 'ویرایش سفارش انجام نشد');
				}

				return response.data;
			})
		);
	}

	cancelOrder(orderId: number): Observable<CancelOrderResponse> {
		return this.http.delete<KalaApiResponse<CancelOrderResponse>>(`${this.ordersUrl}/${orderId}`).pipe(
			map(response => {
				if (!response.isSuccessful) {
					throw new Error(response.error?.message ?? 'لغو سفارش انجام نشد');
				}

				return response.data;
			})
		);
	}

	batchCancelOrder(orderIds: number[]): Observable<MultipleCancelOrderResponse[]> {
		return this.http
			.put<KalaApiResponse<MultipleCancelOrderResponse[]>>(`${this.ordersUrl}/multiple-cancel`, { orderIds })
			.pipe(
				map(response => {
					if (!response.isSuccessful) {
						throw new Error(response.error?.message ?? 'لغو سفارش انجام نشد');
					}

					return response.data ?? [];
				})
			);
	}
}
